import React, { FC, useEffect } from 'react';
import { Col, Row } from 'antd';
import { ButtonSize } from 'enums/ButtonEnums';
import OrganizationModal from 'shared/components/OrganizationModal';
import styles from './farmCommodity.module.scss';
import {
  FormProvider,
  SubmitHandler,
  useFieldArray,
  useForm,
} from 'react-hook-form';
import FieldsRow from 'shared/components/FieldsRow';
import { getStage0Data } from './stage0FarmData';
import { FormType } from 'enums/FieldTypeEnums';
import CircelUploadComponent from 'shared/components/CircleUploadComponent';
import { ReactComponent as AddIcon } from 'assets/icons/add_enclosed.svg';
import CoProductsComponent from './CoProductsComponent';
import Notification from 'shared/components/Notification';
import { NotificationTypes } from 'enums/notificationTypes';
import FarmboxService from 'services/Farmbox/farmbox.service';
import { CoProducts } from 'models/Farmbox/coProducts.model';
import { RcFile, UploadFile } from 'antd/lib/upload';
import { useParams, useSearchParams } from 'react-router-dom';
import { queryParamsList } from '..';
import useFilters from 'shared/hooks/useFilters';
import {
  FARM_TITLE,
  FORM_TITLE,
  STAGE0,
  UPDATE_FORM_TITLE,
} from 'constants/FarmboxConstants/stage0.constants';
import { Commodity } from 'models/Farmbox/commodity.model';
import LoaderAnimation from 'shared/components/LoaderAnimation';
import { yupResolver } from '@hookform/resolvers/yup';
import { farmCommodityValidationSchema } from './farmCommodity.validation';
import { Location } from 'models/ActivityDataEntryLocation/location.model';
import { urlToRcFile } from 'shared/utils/convertIntoBlob';
import useFarmboxRedirections from 'shared/utils/useFarmboxRedirections';
import { DataMappingProps } from 'enums/CreateProductStageNavigationBarEnums';
import isProductDisabled from 'shared/utils/isProductDisabled';
import clsx from 'clsx';
import { adePage } from 'constants/constants';

interface FarmCommodityProps {
  visible: boolean;
  setVisible?: React.Dispatch<React.SetStateAction<boolean>>;
  fetchCommodityList?: (params: {
    page?: number;
    size?: number;
    status?: string;
    search?: string;
    locationId?: string;
    year?: string;
  }) => Promise<void>;
  location?: Location;
  size?: number;
  handleGetCommodity?: () => void;
  isRawMaterial?: boolean;
}

const FarmCommodity: FC<FarmCommodityProps> = props => {
  const {
    visible,
    setVisible,
    fetchCommodityList,
    location,
    size,
    handleGetCommodity,
    isRawMaterial,
  } = props;
  const {
    loading,
    updateLoading,
    createCommodity,
    fetchMetaData,
    metaData,
    getCommodity,
    commodity,
    updatedCommodity,
  } = FarmboxService();
  const { redirectToRespectiveCommodityStage } = useFarmboxRedirections();

  const { id: locationId, status } = useParams();
  const [searchParams] = useSearchParams();
  const queryParams = Object.fromEntries(searchParams.entries());
  const { getParams } = useFilters();
  const { activeTab } = getParams(queryParamsList);
  const { FIELD_NAMES } = STAGE0;

  useEffect(() => {
    fetchMetaData();
  }, []);

  const isDisabled = isProductDisabled(status);

  const fetchCommodity = async () => {
    const initialValues =
      !activeTab && (await getCommodity(Number(locationId)));
    // will change in edit call integration
    if (initialValues) {
      const fetchAndSetImage = async () => {
        const rcFile = await urlToRcFile(initialValues?.imageUrl ?? '');
        methods.setValue('image', rcFile);
        await methods.trigger('image');
      };
      fetchAndSetImage();
    }
    return initialValues
      ? {
          ...initialValues,
          locationId: initialValues?.farmName ?? initialValues.locationId,
        }
      : {
          ...new Commodity(),
          locationId: location?.locationName,
          harvestYear: activeTab,
        };
  };

  const handleModalClose = () => setVisible?.(false);
  const methods = useForm({
    defaultValues: fetchCommodity,
    resolver: yupResolver(farmCommodityValidationSchema),
    mode: 'all',
  });
  const { fields, append, remove } = useFieldArray({
    control: methods.control,
    name: FIELD_NAMES.CO_PRODUCTS,
  });

  const handleAddCoProduct = () => {
    if (fields?.length === 5) {
      Notification({
        message: 'Maximum 5 co-products allowed',
        description: '',
        type: NotificationTypes.INFO,
      });
    } else {
      append(new CoProducts(), {
        focusName: `${FIELD_NAMES.CO_PRODUCTS}.${fields?.length}.name`,
      });
    }
  };

  const formData = getStage0Data({ metaData, isDisabled });

  const onSubmit: SubmitHandler<Commodity> = async data => {
    if (data?.id) {
      const isSuccess = await updatedCommodity(
        {
          ...data,
          locationId: commodity?.locationId,
        },
        Number(locationId)
      );
      if (isSuccess) {
        handleModalClose();
        redirectToRespectiveCommodityStage(
          data?.id,
          DataMappingProps.RAW_MATERIAL,
          status,
          queryParams
        );
        isRawMaterial && handleGetCommodity?.();
      }
    } else {
      const isSuccess = await createCommodity({
        ...data,
        locationId: locationId,
        harvestYear: activeTab,
      });
      if (isSuccess) {
        handleModalClose();
        redirectToRespectiveCommodityStage(
          isSuccess,
          DataMappingProps.RAW_MATERIAL,
          'draft',
          { isFrom: adePage }
        );
        fetchCommodityList?.({ year: activeTab, locationId, size });
      }
    }
  };

  const handleUpload = async (fielName?: RcFile) => {
    methods.setValue('image', fielName);
    await methods.trigger('image');
  };

  const footerProps = {
    confirmButtonProps: {
      children: commodity?.id ? 'Update' : 'Create',
      primary: true,
      size: ButtonSize.SMALL,
      onClick: methods.handleSubmit(onSubmit),
      loading: updateLoading,
    },
    cancelButtonProps: {
      children: 'Cancel',
      secondary: true,
      size: ButtonSize.SMALL,
      onClick: handleModalClose,
    },
    wrapClassName: styles.footerWrapper,
  };

  const formComponent = loading ? (
    <div className={styles.loadingContainer}>
      <LoaderAnimation />
    </div>
  ) : (
    <div className={styles.formContainer}>
      <FormProvider {...methods}>
        <Row gutter={[24, 24]}>
          <div className={styles.uploadComponentContainer}>
            <div>
              <CircelUploadComponent
                file={[methods.getValues('image')] as UploadFile[]}
                setImage={handleUpload}
                errorMessage={methods.formState.errors.image?.message}
                isDisabled={isDisabled}
              />
            </div>
          </div>
          <Row gutter={[24, 24]} className={styles.formFieldContainer}>
            <Col span={24}>
              <FieldsRow {...formData} formType={FormType.REACT_HOOK_FORMS} />
            </Col>
          </Row>
          <Row className={styles.coProductsContainer}>
            <div
              className={clsx(styles.addContainer, {
                [styles.hideAction]: isDisabled,
              })}
              onClick={handleAddCoProduct}>
              <AddIcon /> Add a co product
            </div>
          </Row>
          {fields.map((coProduct, index) => (
            <CoProductsComponent
              key={coProduct.id}
              remove={remove}
              index={index}
              isDisabled={isDisabled}
            />
          ))}
        </Row>
      </FormProvider>
    </div>
  );
  return (
    <OrganizationModal
      width={700}
      visible={visible}
      title={
        isDisabled ? FARM_TITLE : commodity?.id ? UPDATE_FORM_TITLE : FORM_TITLE
      }
      isExtraSpace
      onClose={handleModalClose}
      modalFooterProps={isDisabled ? undefined : footerProps}
      children={formComponent}
      wrapClassName={styles.farmCommoditymodalContainer}
    />
  );
};

export default FarmCommodity;
